import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/tienda.css';
import Logo from '../assets/logo.png';
import { useCarrito } from '../context/carritoContext';
import { getProducts } from '../services/catalogApi';
import { tieneVariantes, getVarianteDefault, getVariantesProducto } from '../services/catalogApi';
import { productoPath } from '../utils/slug';

const ProductosLinea = ({
  lineaId,
  categoriaId,
  tagId,
  tagIds,
  nombre,
  kicker = 'Categoría destacada',
  // Lista ya resuelta (ej. productos relacionados semánticamente con otro
  // producto): si se manda, se usa tal cual y no se dispara el fetch por
  // línea/categoría/tags.
  productos: productosPrecargados,
}) => {
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agregadoIds, setAgregadoIds] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [fotoIndices, setFotoIndices] = useState({});

  const gridRef = useRef(null);
  const navigate = useNavigate();
  const { agregarAlCarrito, carrito } = useCarrito();

  const fotosUnicasDe = (item) => {
    const variantes = getVariantesProducto(item);
    const fotos = variantes.map((v) => v.artFoto?.[0] ?? v.artFoto).filter(Boolean);
    return Array.from(new Set(fotos));
  };

  const updateScrollButtons = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const maxScrollLeft = grid.scrollWidth - grid.clientWidth;
    const current = grid.scrollLeft;

    setCanScrollLeft(current > 4);
    setCanScrollRight(current < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    if (productosPrecargados) {
      setArticulos(productosPrecargados);
      setLoading(false);
      return;
    }

    setLoading(true);

    getProducts({
      lineaId,
      categoriaId,
      tagId,
      tagIds,
    })
      .then(setArticulos)
      .catch(() => setArticulos([]))
      .finally(() => setLoading(false));
  }, [lineaId, categoriaId, tagId, tagIds, productosPrecargados]);

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [articulos, updateScrollButtons]);

  useEffect(() => {
    const productosConFotosMultiples = articulos.filter((item) => fotosUnicasDe(item).length > 1);

    if (productosConFotosMultiples.length === 0) return;

    const intervalo = setInterval(() => {
      setFotoIndices((prev) => {
        const next = { ...prev };
        productosConFotosMultiples.forEach((item) => {
          const totalFotos = getVariantesProducto(item).length;
          const actual = prev[item.artId] ?? 0;
          next[item.artId] = (actual + 1) % totalFotos;
        });
        return next;
      });
    }, 2200);

    return () => clearInterval(intervalo);
  }, [articulos]);

  const scrollDistance = 282 * 2;

  const handleScrollLeft = () => {
    gridRef.current?.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
    setTimeout(updateScrollButtons, 240);
  };

  const handleScrollRight = () => {
    gridRef.current?.scrollBy({ left: scrollDistance, behavior: 'smooth' });
    setTimeout(updateScrollButtons, 240);
  };

  const handleCardClick = (item) => {
    navigate(productoPath(item));
  };

  const handleAgregarCarrito = (e, item, variante) => {
    e.stopPropagation();

    const yaEnCarrito = carrito.some((p) => p.artId === variante.varId);
    if (yaEnCarrito) return;

    const itemCarrito = {
      ...item,
      artId: variante.varId,
      artNom: variante.etiqueta ? `${item.artNom} - ${variante.etiqueta}` : item.artNom,
      artPrecio: variante.artPrecio,
      artDescuento: variante.artDescuento,
      artFoto: variante.artFoto,
    };
    delete itemCarrito.variantes;

    agregarAlCarrito(itemCarrito);
    setAgregadoIds((prev) => [...prev, variante.varId]);

    const evento = new Event('carrito-actualizado');
    window.dispatchEvent(evento);

    setTimeout(() => {
      setAgregadoIds((prev) => prev.filter((id) => id !== variante.varId));
    }, 2000);
  };

  const handleVerOpciones = (e, item) => {
    e.stopPropagation();
    navigate(productoPath(item));
  };

  if (loading) return <p>Cargando productos de linea {nombre}...</p>;

  // Con lista precargada (ej. relacionados de otro producto) una sección
  // vacía no aporta nada: se oculta en vez de mostrar un carrusel vacío.
  if (productosPrecargados && articulos.length === 0) return null;

  return (
    <section className="seccion-productos">
      <div className="linea-header">
        <div>
          <p className="linea-kicker">{kicker}</p>
          <h2 className="linea-h2">{nombre}</h2>
        </div>
      </div>
      <hr className="linea-hr" />

      {canScrollLeft && (
        <button className="btn-scroll btn-scroll-left" onClick={handleScrollLeft} aria-label="Desplazar a la izquierda">
          &#8249;
        </button>
      )}

      <div ref={gridRef} className="grid-productos" onScroll={updateScrollButtons}>
        {articulos.length === 0 && <p>No hay productos disponibles para esta linea.</p>}
        {articulos.map((item) => {
          const variantesItem = getVariantesProducto(item);
          const conVariantes = tieneVariantes(item);
          const variantePrecio = getVarianteDefault(item);

          if (!variantePrecio) return null;

          const indiceFoto = fotoIndices[item.artId] ?? 0;
          const varianteFoto = variantesItem[indiceFoto] ?? variantesItem[0];
          const foto = varianteFoto.artFoto?.[0] ?? varianteFoto.artFoto ?? '/img/default.jpg';
          const fotosItem = variantesItem.map((v) => v.artFoto?.[0] ?? v.artFoto ?? '/img/default.jpg');
          const ciclaFotos = fotosUnicasDe(item).length > 1;

          const yaEnCarrito = carrito.some((p) => p.artId === variantePrecio.varId);
          const mostrarAgregado = agregadoIds.includes(variantePrecio.varId) || yaEnCarrito;

          const porcentajeDescuento = variantePrecio.artDescuento > 0 && variantePrecio.artPrecio > 0
            ? Math.round((variantePrecio.artDescuento / variantePrecio.artPrecio) * 100)
            : 0;

          return (
            <div key={item.artId} className="producto-carta" onClick={() => handleCardClick(item)}>
              <div className="carta-media">
                {item.artCZ && <img src={Logo} alt="Producto CZ" className="cz-icon" />}
                {porcentajeDescuento > 0 && (
                  <span className="carta-badge">-{porcentajeDescuento}%</span>
                )}
                {ciclaFotos ? (
                  fotosItem.map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt={item.artNom}
                      className={`producto-foto producto-foto-stack ${indiceFoto === idx ? 'activa' : ''}`}
                      loading="lazy"
                    />
                  ))
                ) : (
                  <img
                    src={foto}
                    alt={item.artNom}
                    className="producto-foto"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="producto-info">
                <h3>{item.artNom}</h3>
                <div className="precio-accion">
                  <div className="precios">
                    {conVariantes && <span className="precio-desde">Desde</span>}
                    {variantePrecio.artDescuento > 0 ? (
                      <p className="precio-linea">
                        <span className="precio-descuento">
                          ${(variantePrecio.artPrecio - variantePrecio.artDescuento).toFixed(2)}
                        </span>
                        <span className="precio-original">
                          ${variantePrecio.artPrecio.toFixed(2)}
                        </span>
                      </p>
                    ) : (
                      <p className="precio-linea">
                        <span className="precio-normal">
                          ${variantePrecio.artPrecio.toFixed(2)}
                        </span>
                      </p>
                    )}
                  </div>
                  {conVariantes ? (
                    <button className="button-add compact" onClick={(e) => handleVerOpciones(e, item)}>
                      Ver opciones
                    </button>
                  ) : (
                    <button
                      className={`button-add compact ${mostrarAgregado ? 'is-added' : ''}`}
                      onClick={(e) => handleAgregarCarrito(e, item, variantePrecio)}
                      disabled={mostrarAgregado}
                    >
                      {mostrarAgregado ? 'Agregado' : 'Agregar'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {canScrollRight && (
        <button className="btn-scroll btn-scroll-right" onClick={handleScrollRight} aria-label="Desplazar a la derecha">
          &#8250;
        </button>
      )}
    </section>
  );
};

export default ProductosLinea;
